// PDF export utility for dashboard — captures the dashboard as a visual screenshot

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const EXPORT_WIDTH = 1400;

export const exportDashboardPdf = async (element: HTMLElement, bgColor: string): Promise<void> => {
  // Hide elements marked with data-export-hide and save their original display
  const hiddenEls = element.querySelectorAll<HTMLElement>('[data-export-hide]');
  const originalDisplays: string[] = [];
  hiddenEls.forEach(el => {
    originalDisplays.push(el.style.display);
    el.style.display = 'none';
  });

  // Force a consistent width so the PDF looks the same regardless of viewport
  const originalWidth = element.style.width;
  const originalMinWidth = element.style.minWidth;
  const originalMaxWidth = element.style.maxWidth;
  element.style.width = `${EXPORT_WIDTH}px`;
  element.style.minWidth = `${EXPORT_WIDTH}px`;
  element.style.maxWidth = `${EXPORT_WIDTH}px`;

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: bgColor,
    windowWidth: EXPORT_WIDTH,
  });

  // Restore original styles
  element.style.width = originalWidth;
  element.style.minWidth = originalMinWidth;
  element.style.maxWidth = originalMaxWidth;
  hiddenEls.forEach((el, i) => { el.style.display = originalDisplays[i]; });

  const imgData = canvas.toDataURL('image/png');
  const imgWidth = canvas.width;
  const imgHeight = canvas.height;

  // Use landscape if the content is wider than tall
  const orientation = imgWidth > imgHeight ? 'l' : 'p';
  const doc = new jsPDF(orientation, 'mm', 'a4');

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const usableWidth = pageWidth - margin * 2;

  // Fill entire PDF page with the theme background color
  const fillPageBg = () => {
    doc.setFillColor(bgColor);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
  };

  // Scale image to fit page width
  const ratio = usableWidth / imgWidth;
  const scaledHeight = imgHeight * ratio;

  // If the image fits on one page, place it directly
  if (scaledHeight <= pageHeight - margin * 2) {
    fillPageBg();
    doc.addImage(imgData, 'PNG', margin, margin, usableWidth, scaledHeight);
  } else {
    // Split across multiple pages
    const pageContentHeight = pageHeight - margin * 2;
    // How many source pixels fit per page
    const sourceHeightPerPage = pageContentHeight / ratio;
    let srcY = 0;

    while (srcY < imgHeight) {
      fillPageBg();
      const sliceHeight = Math.min(sourceHeightPerPage, imgHeight - srcY);

      // Create a canvas slice for this page
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = imgWidth;
      sliceCanvas.height = sliceHeight;
      const ctx = sliceCanvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(canvas, 0, srcY, imgWidth, sliceHeight, 0, 0, imgWidth, sliceHeight);
        const sliceData = sliceCanvas.toDataURL('image/png');
        const renderedHeight = sliceHeight * ratio;
        doc.addImage(sliceData, 'PNG', margin, margin, usableWidth, renderedHeight);
      }

      srcY += sliceHeight;
      if (srcY < imgHeight) {
        doc.addPage();
      }
    }
  }

  doc.save(`sudokuvs-dashboard-${new Date().toISOString().split('T')[0]}.pdf`);
};
